import { Actor, Color, Vector, Line, vec, CollisionType } from 'excalibur';
import { Shell } from './Shell';
import { controller } from '../../shared/controls/controller';
import { leftKeyboard } from '../../shared/controls/leftKeyboard';
import { rightKeyboard } from '../../shared/controls/rightKeyboard';
export class Mortar extends Actor {
    public angle: number = 90;
    private barrel: Actor;
    public facing: 'left' | 'right';
    private mortarController: ReturnType<typeof controller> | ReturnType<typeof leftKeyboard> | ReturnType<typeof rightKeyboard>;
    private fireCooldown: number = 0;
    constructor({ x, y, color, facing, mortarController }: { x: number, y: number, color: Color, facing: 'left' | 'right', mortarController: ReturnType<typeof controller> | ReturnType<typeof leftKeyboard> | ReturnType<typeof rightKeyboard> }) {
        super({ x, y, width: 48, height: 48, anchor: vec(0, 1) });
        this.body.collisionType = CollisionType.Active;
        this.facing = facing;
        this.mortarController = mortarController;

        // Add a box collider to the base of the mortar (width: 48, height: 16)
        this.collider.useBoxCollider(48, 16);

        // Base plate: horizontal line at the bottom of the mortar
        const basePlate = new Actor({
            x: 0,
            y: 0,
            width: 48,
            height: 8,
            anchor: vec(0, 1)
        });
        const baseLine = new Line({
            start: vec(0, 0),
            end: vec(48, 0),
            color,
            thickness: 8
        });
        basePlate.graphics.use(baseLine);
        this.addChild(basePlate);

        // Barrel: vertical line, anchored at left or right end of base plate
        let barrelAnchor, barrelX;
        if (facing === 'right') {
            barrelAnchor = vec(0, 1); // left bottom
            barrelX = 0;
        } else {
            barrelAnchor = vec(1, 1); // right bottom
            barrelX = 48;
        }
        this.barrel = new Actor({
            x: barrelX,
            y: 0,
            width: 8,
            height: 40,
            anchor: barrelAnchor
        });
        const barrelLine = new Line({
            start: vec(0, 0),
            end: vec(0, -40),
            color,
            thickness: 8
        });
        this.barrel.graphics.use(barrelLine);
        this.addChild(this.barrel);
    }

    handleRotationAndFiring(delta: number) {
        if (this.fireCooldown !== 0) {
            this.fireCooldown -= delta;
        }
        if (this.fireCooldown < 0) {
            this.fireCooldown = 0;
        }
        // Clamp angle for both players: 0 (flat) to 90 (up)
        this.angle = Math.max(0, Math.min(90, this.angle));
        // Always update info label with current values
        if (this.mortarController.leftStick.x < 0) {
            // Increase angle
            if (this.facing === 'right') {
                this.increaseAngle(40 * (delta / 1000));
            } else {
                this.decreaseAngle(40 * (delta / 1000));
            }
        } else if (this.mortarController.leftStick.x > 0) {
            // Decrease angle
            if (this.facing === 'right') {
                this.decreaseAngle(40 * (delta / 1000));
            } else {
                this.increaseAngle(40 * (delta / 1000));
            }
        }
        if (this.mortarController.rightTrigger.pressed && this.fireCooldown === 0) {
            // Fire
            const shell = this.fire();
            if (this.scene && shell) {
                this.scene.add(shell);
            }
            this.fireCooldown = 3000; // 3 second cooldown
        }
    }
    onPreUpdate() {
        // For both facings, 90° is straight up
        // For right facing (p1), decrease angle rotates barrel clockwise (down to 0° flat)
        // For left facing (p2), decrease angle rotates barrel counterclockwise (down to 0° flat)
        if (this.facing === 'right') {
            this.barrel.rotation = (90 - this.angle) * Math.PI / 180;
        } else {
            this.barrel.rotation = -(90 - this.angle) * Math.PI / 180;
        }
    }

    decreaseAngle(delta: number) {
        this.angle = Math.max(0, this.angle - delta);
    }

    increaseAngle(delta: number) {
        this.angle = Math.min(90, this.angle + delta);
    }

    fire(): Shell {
        console.log(`Firing mortar at angle ${this.angle}° facing ${this.facing}`);
        console.log('[Mortar] pos:', this.pos, 'angle:', this.angle, 'facing:', this.facing);
        let power = 1500;
        // 1. Get the barrel's world rotation (barrel.rotation + this.rotation)
        const worldBarrelAngle = this.barrel.rotation + this.rotation;

        // 2. The barrel base in world coordinates
        let barrelBase = this.pos.add(this.barrel.pos.rotate(this.rotation));
        // 3. The tip is 40px from the base, in the direction of worldBarrelAngle
        let tipOffset = new Vector(0, -40).rotate(worldBarrelAngle);
        let tip = barrelBase.add(tipOffset);

        // 4. Velocity: direction from base to tip, normalized and scaled by power
        let direction = tipOffset.normalize();
        const velocity = direction.scale(power);
        return new Shell(tip.x, tip.y, velocity, this.color);
    }
    getFiringAngleRadians(): number {
        if (this.facing === 'right') {
            // 0° is flat right, 90° is straight up
            return (90 - this.angle) * Math.PI / 180;
        } else {
            // 0° is flat left, 90° is straight up (mirror horizontally)
            return (this.angle - 90) * Math.PI / 180;
        }
    }
}